import { useEffect, useRef, useState } from "react";
import LearnMore from "@/components/LearnMore";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Moon, Sparkles, Loader2, Headphones } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [8, 12, 15, 20, 30];

const srcOf = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function NightAudio() {
  const [catalogue, setCatalogue] = useState(null);
  const [situation, setSituation] = useState("");
  const [transformation, setTransformation] = useState("");
  const [intent, setIntent] = useState("");
  const [duration, setDuration] = useState(12);
  const [voice, setVoice] = useState("soulumo_default_male");
  const [current, setCurrent] = useState(null); // active installation being built/played
  const [building, setBuilding] = useState(false);
  const [library, setLibrary] = useState([]);
  const pollRef = useRef(null);
  const builderRef = useRef(null);

  useEffect(() => {
    api.get("/soulumo/catalogue").then((r) => {
      setCatalogue(r.data);
      const ds = r.data?.durations_minutes || [];
      if (ds.length && !ds.includes(12)) setDuration(ds.reduce((a, b) => (Math.abs(b - 12) < Math.abs(a - 12) ? b : a)));
    }).catch(() => {});
    loadLibrary();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const loadLibrary = () => api.get("/soulumo/installations").then((r) => setLibrary(r.data.items || [])).catch(() => {});

  const poll = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const started = Date.now();
    pollRef.current = setInterval(async () => {
      if (Date.now() - started > 10 * 60 * 1000) { clearInterval(pollRef.current); pollRef.current = null; setBuilding(false); toast.error("That one is taking too long — please try again."); return; }
      try {
        const { data } = await api.get(`/soulumo/installations/${id}`);
        setCurrent(data);
        if (data.status === "ready" || data.status === "failed") {
          clearInterval(pollRef.current); pollRef.current = null;
          setBuilding(false);
          loadLibrary();
          if (data.status === "failed") toast.error("That one didn't render — please try again.");
        }
      } catch { /* keep polling */ }
    }, 5000);
  };

  const generate = async () => {
    if (!situation.trim() || !transformation.trim()) {
      toast.error("Tell me the moment and how you want to feel."); return;
    }
    setBuilding(true); setCurrent(null);
    try {
      const { data } = await api.post("/soulumo/installations", {
        situation, transformation, intent: intent || null,
        duration_minutes: duration, voice_id: voice,
      });
      setCurrent(data);
      poll(data.installation_id);
    } catch (e) {
      setBuilding(false);
      toast.error("Couldn't start your audio. Please try again.");
    }
  };

  const statusLabel = { queued: "Preparing your session…", rendering: "Composing your audio…", ready: "Ready", failed: "Failed" };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="nightaudio-page">
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full opacity-20"
        style={{ background: "radial-gradient(circle, hsl(215 60% 55% / 0.5) 0%, transparent 62%)", filter: "blur(70px)" }} />
      <Navbar />
      <main className="relative max-w-2xl mx-auto px-5 sm:px-8 pt-8 pb-32">
        <div className="flex items-center gap-3 mb-2">
          <Moon className="w-6 h-6 text-primary" />
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Tonight's Audio</h1>
        </div>
        <div className="mb-4"><LearnMore area="audio" onCta={() => setTimeout(() => builderRef.current?.scrollIntoView({ behavior: "smooth" }), 100)} /></div>
        <p className="text-muted-foreground mb-8 text-sm max-w-lg">
          A personalised installation, composed for you by Xcelio, in your coach's voice. Describe the moment you're facing and who you want to be in it — then listen tonight, headphones on, before sleep.
        </p>

        {/* builder */}
        <motion.div ref={builderRef} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-6 mb-8 border border-white/10 space-y-5">
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-primary font-medium">The moment</label>
            <Textarea value={situation} onChange={(e) => setSituation(e.target.value)} maxLength={800}
              placeholder="e.g. First date on Friday — I go quiet and try too hard when I like someone."
              className="mt-2 rounded-2xl min-h-[90px]" data-testid="na-situation" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-primary font-medium">Who you want to be</label>
            <Input value={transformation} onChange={(e) => setTransformation(e.target.value)} maxLength={400}
              placeholder="e.g. calm, present, nothing to prove"
              className="mt-2 rounded-full" data-testid="na-transformation" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Intent</label>
              <select value={intent} onChange={(e) => setIntent(e.target.value)} data-testid="na-intent"
                className="mt-2 w-full rounded-full bg-muted/50 border border-white/10 px-4 py-2.5 text-sm">
                <option value="">Auto</option>
                {(catalogue?.intents || []).map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Length</label>
              <select value={duration} onChange={(e) => setDuration(Number(e.target.value))} data-testid="na-duration"
                className="mt-2 w-full rounded-full bg-muted/50 border border-white/10 px-4 py-2.5 text-sm">
                {(catalogue?.durations_minutes || DURATIONS).map((d) => <option key={d} value={d}>{d} min</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Voice</label>
            <div className="mt-2 flex gap-2">
              {[{ id: "soulumo_default_male", label: "Male" }, { id: "soulumo_default_female", label: "Female" }].map((v) => (
                <button key={v.id} onClick={() => setVoice(v.id)} data-testid={`na-voice-${v.label.toLowerCase()}`}
                  className={`px-4 py-2 rounded-full text-sm border transition-colors ${voice === v.id ? "bg-primary/20 border-primary/60 text-primary" : "border-white/15 text-muted-foreground hover:bg-white/5"}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>

          <Button onClick={generate} disabled={building} className="w-full rounded-full h-12 text-base" data-testid="na-generate-btn">
            {building ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</> : <><Sparkles className="w-4 h-4 mr-2" /> Prepare my audio</>}
          </Button>
        </motion.div>

        {/* active / result */}
        {current && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="glass rounded-3xl p-6 mb-8 border border-primary/20" data-testid="na-current">
            <div className="flex items-center gap-2 mb-3">
              <Headphones className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium" data-testid="na-status">
                {statusLabel[current.status] || current.status}
              </span>
              {current.status !== "ready" && current.status !== "failed" && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
            </div>
            {current.status === "ready" && current.audio_url ? (
              <audio controls autoPlay src={srcOf(current.audio_url)} className="w-full" data-testid="na-player" />
            ) : current.status === "failed" ? (
              <p className="text-sm text-muted-foreground">Something went wrong composing this one. Try again.</p>
            ) : (
              <p className="text-sm text-muted-foreground">This usually takes 30–120 seconds. You can stay on this page.</p>
            )}
          </motion.div>
        )}

        {/* library */}
        {library.length > 0 && (
          <div data-testid="na-library">
            <h2 className="text-lg font-medium mb-4">Your installations</h2>
            <div className="space-y-3">
              {library.map((it) => (
                <div key={it.installation_id} className="glass rounded-2xl p-4 border border-white/10" data-testid={`na-item-${it.installation_id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-sm font-medium capitalize">{it.title || it.intent || "Installation"}</div>
                    <div className="text-xs text-muted-foreground">{Math.round((it.duration_sec || 0) / 60)} min</div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-1">{it.transformation}</p>
                  {it.status === "ready" && it.audio_url ? (
                    <audio controls src={srcOf(it.audio_url)} className="w-full h-9" data-testid={`na-play-${it.installation_id}`} />
                  ) : (
                    <span className="text-xs text-muted-foreground">{statusLabel[it.status] || it.status}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
